import { useEffect, useLayoutEffect, useRef } from "react";

const EMPTY_SNAPSHOT = {
  itemCount: 0,
  scrollTop: 0,
  scrollHeight: 0,
  shouldRestore: false,
};

export const useStablePrependScroll = ({
  itemCount,
  enabled = true,
  resetKey,
  topThreshold = 24,
}) => {
  const containerRef = useRef(null);
  const snapshotRef = useRef({
    ...EMPTY_SNAPSHOT,
    itemCount,
  });
  const itemCountRef = useRef(itemCount);

  itemCountRef.current = itemCount;

  useEffect(() => {
    snapshotRef.current = {
      ...EMPTY_SNAPSHOT,
      itemCount: itemCountRef.current,
    };

    const node = containerRef.current;
    if (node) {
      node.scrollTop = 0;
    }
  }, [resetKey]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const updateSnapshot = () => {
      snapshotRef.current = {
        itemCount: itemCountRef.current,
        scrollTop: node.scrollTop,
        scrollHeight: node.scrollHeight,
        shouldRestore: enabled && node.scrollTop > topThreshold,
      };
    };

    updateSnapshot();
    node.addEventListener("scroll", updateSnapshot, { passive: true });

    return () => {
      node.removeEventListener("scroll", updateSnapshot);
    };
  }, [enabled, resetKey, topThreshold]);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const previous = snapshotRef.current;
    const didPrependItems = itemCount > previous.itemCount;

    if (enabled && previous.shouldRestore && didPrependItems) {
      const heightDelta = node.scrollHeight - previous.scrollHeight;
      if (heightDelta > 0) {
        node.scrollTop = previous.scrollTop + heightDelta;
      }
    }

    snapshotRef.current = {
      itemCount,
      scrollTop: node.scrollTop,
      scrollHeight: node.scrollHeight,
      shouldRestore: enabled && node.scrollTop > topThreshold,
    };
  }, [enabled, itemCount, resetKey, topThreshold]);

  return containerRef;
};
